import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Slide from "react-slick";
import useUser from 'src/hook/data/useUser';
import { ToIOSafeHash } from 'src/utils/IOSafeHash';
import { Word } from '../../../../model/Word';
import { ProTipData, Sense } from '../../../../model/WordData';
import { SliderSettings } from '../../../../utils/SliderSetting';
import { DirectIconLocation, GetTipsImageUrl } from "../../../../utils/URLUtils";
import ProTip from './ProTip/ProTip';
import styles from "./ProTips.module.scss";


interface ProTipsProps {
    word: Word;
    sense: Sense | undefined;
    isFromDefinition?: boolean;
}

const ProTips = ({ word, sense, isFromDefinition }: ProTipsProps) => {
    const [tips, setTips] = useState<ProTipData[]>([]);
    const [isStop, setIsStop] = useState(false);
    const { userInfo, isProUser } = useUser();
    const { t } = useTranslation();

    const fetchData = useCallback(async () => {
        const tp = sense?.tp?.split('\n');
        const isPrimarySense = sense?.id === word.PrimarySenseId;
        const tipsPromises = tp?.map(async (tip, index) => {
            const tipSections = tip.split('|');
            const title = tipSections[0] ?? '';
            const description = isProUser() ? tipSections[1] ?? ''
                : `Get Pro for advanced tips to use ${word.Text} correctly.`;
            const example = isProUser() ? tipSections[2] ?? '' :
                tipSections[2] ? tipSections[2].substring(0, 40)
                    + `... 🔒` : '';
            let imageUrl = '';
            if (isPrimarySense) {
                imageUrl =
                    GetTipsImageUrl(await ToIOSafeHash(tipSections[2].trim()), !isProUser());
            }
            return { Title: title, Description: description, Example: example, ImageUrl: imageUrl };
        });

        const tips = await Promise.all(tipsPromises ?? []);


        if (sense?.cl?.length && sense?.cl[0] !== '') {
            const oftenAppearAs = {
                Title: 'Collocations',
                Description: 'It often appears as:',
                Example: sense?.cl.map(cl => `◆${cl}`).join('\n'),
                ImageUrl: ''
            };
            tips.push(oftenAppearAs);
        }

        setTips(tips);
    }, [sense, word, isProUser]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);


    if (tips?.length === 0) return null;

    return (
        <section className={styles.proTips}>
            <div className={styles.title}>
                <img src={DirectIconLocation('bulb')} alt="Pro Tips" />
                <h1>{t('Pro Tips')}</h1>
            </div>
            <div className="slider-container">
                <Slide {...SliderSettings()}
                    beforeChange={() => setIsStop(true)}
                    afterChange={() => setIsStop(false)}>
                    {tips?.map((item, index) => (
                        <ProTip key={index} isStop={isStop}
                            word={word} tip={item} isFromDefinition={isFromDefinition} />
                    ))}
                </Slide>
            </div>
        </section>
    );
}

export default ProTips;